import type { AnalyticsConsentDecision, AnalyticsConsentState } from "./AnalyticsConsentState";
import { clearKnownAnalyticsCookies } from "./AnalyticsCookieCleanup";
import { loadGoogleTagManager } from "./GoogleTagManagerLoader";
import { ConsentStatistics } from "./ConsentStatistics";
import { ConsentAction } from "../Generated/ConsentAction";
import { ConsentPromptKind } from "../Generated/ConsentPromptKind";

const consentLayerTeardownDelayMilliseconds = 500;

interface AnalyticsConsentUiOptions {
    documentRef: Document;
    windowRef: Window;
    overlay: HTMLElement;
    panel: HTMLElement;
    copyArea: HTMLElement;
    acceptButton: HTMLButtonElement;
    rejectButton: HTMLButtonElement;
    settingsButton: HTMLButtonElement;
    containerId: string;
    consentState: Pick<AnalyticsConsentState, "getDecision" | "setDecision" | "announceCurrentDecision">;
    loadGtm?: typeof loadGoogleTagManager;
    clearCookies?: typeof clearKnownAnalyticsCookies;
    reload?: () => void;
}

export class AnalyticsConsentUi {
    private readonly documentRef: Document;
    private readonly windowRef: Window;
    private readonly overlay: HTMLElement;
    private readonly panel: HTMLElement;
    private readonly copyArea: HTMLElement;
    private readonly acceptButton: HTMLButtonElement;
    private readonly rejectButton: HTMLButtonElement;
    private readonly settingsButton: HTMLButtonElement;
    private readonly containerId: string;
    private readonly consentState: AnalyticsConsentUiOptions["consentState"];
    private readonly loadGtm: typeof loadGoogleTagManager;
    private readonly clearCookies: typeof clearKnownAnalyticsCookies;
    private readonly reload: () => void;
    private readonly statistics: ConsentStatistics;
    private previouslyFocusedElement: HTMLElement | null = null;
    private choicePending = false;
    private pendingActionTimer: number | null = null;

    constructor(options: AnalyticsConsentUiOptions) {
        this.documentRef = options.documentRef;
        this.windowRef = options.windowRef;
        this.overlay = options.overlay;
        this.panel = options.panel;
        this.copyArea = options.copyArea;
        this.acceptButton = options.acceptButton;
        this.rejectButton = options.rejectButton;
        this.settingsButton = options.settingsButton;
        this.containerId = options.containerId;
        this.consentState = options.consentState;
        this.loadGtm = options.loadGtm ?? loadGoogleTagManager;
        this.clearCookies = options.clearCookies ?? clearKnownAnalyticsCookies;
        this.reload = options.reload ?? (() => this.windowRef.location.reload());
        this.statistics = new ConsentStatistics(this.documentRef, this.windowRef, this.overlay);
    }

    connect(): void {
        this.documentRef.addEventListener("keydown", this.handleKeyDown);
        this.copyArea.addEventListener("scroll", this.updateCopyScrollbar);
        this.windowRef.addEventListener("resize", this.updateCopyScrollbar);
        const decision = this.consentState.getDecision();
        this.showSelectedDecision(decision);

        if (decision === "granted") {
            this.loadGtm(this.documentRef, this.windowRef, this.containerId);
            this.consentState.announceCurrentDecision();
            this.close(false);
        } else if (decision === "denied") {
            this.close(false);
        } else {
            this.open(false);
        }
    }

    disconnect(): void {
        this.statistics.disconnect();
        this.documentRef.removeEventListener("keydown", this.handleKeyDown);
        this.copyArea.removeEventListener("scroll", this.updateCopyScrollbar);
        this.windowRef.removeEventListener("resize", this.updateCopyScrollbar);
        if (this.pendingActionTimer !== null) {
            this.windowRef.clearTimeout(this.pendingActionTimer);
            this.pendingActionTimer = null;
        }
        this.choicePending = false;
        this.acceptButton.disabled = false;
        this.rejectButton.disabled = false;
        this.documentRef.body.classList.remove("analytics-consent-open");
        this.documentRef.body.classList.remove("analytics-consent-settings-visible");
    }

    openSettings(): void {
        this.open(true);
    }

    accept(): void {
        if (this.choicePending) return;
        this.statistics.choose(ConsentAction.Accepted);
        this.choicePending = true;
        this.acceptButton.disabled = true;
        this.rejectButton.disabled = true;
        this.loadGtm(this.documentRef, this.windowRef, this.containerId);
        this.consentState.setDecision("granted");
        this.showSelectedDecision("granted");
        this.pendingActionTimer = this.windowRef.setTimeout(
            () => {
                this.pendingActionTimer = null;
                this.close(true);
            },
            consentLayerTeardownDelayMilliseconds
        );
    }

    reject(): void {
        if (this.choicePending) return;
        this.statistics.choose(ConsentAction.Rejected);
        this.choicePending = true;
        this.acceptButton.disabled = true;
        this.rejectButton.disabled = true;
        const previousDecision = this.consentState.getDecision();
        this.consentState.setDecision("denied");
        this.showSelectedDecision("denied");

        if (previousDecision === "granted") {
            this.clearCookies(this.documentRef, this.windowRef.location.hostname);
            this.pendingActionTimer = this.windowRef.setTimeout(
                () => {
                    this.pendingActionTimer = null;
                    this.reload();
                },
                consentLayerTeardownDelayMilliseconds
            );
        } else {
            this.pendingActionTimer = this.windowRef.setTimeout(
                () => {
                    this.pendingActionTimer = null;
                    this.close(true);
                },
                consentLayerTeardownDelayMilliseconds
            );
        }
    }

    private readonly handleKeyDown = (event: KeyboardEvent): void => {
        if (!this.overlay.isConnected || this.overlay.hidden) return;

        if (event.key === "Escape") {
            event.preventDefault();
            this.reject();
            return;
        }

        if (event.key !== "Tab") return;

        const focusableElements = Array.from(this.panel.querySelectorAll<HTMLElement>(
            "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
        )).filter(element => !element.hidden);
        if (focusableElements.length === 0) {
            event.preventDefault();
            this.panel.focus();
            return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && this.documentRef.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && this.documentRef.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    private readonly updateCopyScrollbar = (): void => {
        const viewportHeight = this.copyArea.clientHeight;
        const contentHeight = this.copyArea.scrollHeight;
        const overflowing = viewportHeight > 0 && contentHeight > viewportHeight + 1;
        this.copyArea.classList.toggle("analytics-consent__body--overflowing", overflowing);

        if (!overflowing) {
            this.copyArea.style.removeProperty("--analytics-consent-scrollbar-thumb-height");
            this.copyArea.style.removeProperty("--analytics-consent-scrollbar-thumb-offset");
            return;
        }

        const thumbHeight = Math.max(24, Math.round(viewportHeight * viewportHeight / contentHeight));
        const maximumThumbOffset = Math.max(0, viewportHeight - thumbHeight);
        const maximumScrollOffset = contentHeight - viewportHeight;
        const thumbOffset = Math.round(
            maximumThumbOffset * this.copyArea.scrollTop / maximumScrollOffset * 10
        ) / 10;
        this.copyArea.style.setProperty("--analytics-consent-scrollbar-thumb-height", `${thumbHeight}px`);
        this.copyArea.style.setProperty("--analytics-consent-scrollbar-thumb-offset", `${thumbOffset}px`);
    };

    private open(rememberFocus: boolean): void {
        const decision = this.consentState.getDecision();
        if (rememberFocus) {
            this.previouslyFocusedElement = this.documentRef.activeElement instanceof HTMLElement
                ? this.documentRef.activeElement
                : null;
        }
        this.showSelectedDecision(decision);
        this.overlay.hidden = false;
        this.overlay.setAttribute("aria-hidden", "false");
        this.settingsButton.hidden = decision === null;
        if (decision === null) {
            this.documentRef.body.classList.remove("analytics-consent-settings-visible");
        }
        this.documentRef.body.classList.add("analytics-consent-open");
        this.updateCopyScrollbar();

        const initialFocus = decision === "granted"
            ? this.acceptButton
            : this.rejectButton;
        initialFocus.focus({ preventScroll: true });
        this.statistics.open(rememberFocus ? ConsentPromptKind.Settings : ConsentPromptKind.Initial);
    }

    private close(restoreFocus: boolean): void {
        this.choicePending = false;
        this.overlay.hidden = true;
        this.overlay.setAttribute("aria-hidden", "true");
        this.settingsButton.hidden = false;
        this.acceptButton.disabled = false;
        this.rejectButton.disabled = false;
        this.documentRef.body.classList.remove("analytics-consent-open");
        this.documentRef.body.classList.add("analytics-consent-settings-visible");

        if (restoreFocus && this.previouslyFocusedElement?.isConnected) {
            this.previouslyFocusedElement.focus({ preventScroll: true });
        }
        this.previouslyFocusedElement = null;
    }

    private showSelectedDecision(decision: AnalyticsConsentDecision | null): void {
        this.acceptButton.setAttribute("aria-pressed", String(decision === "granted"));
        this.rejectButton.setAttribute("aria-pressed", String(decision === "denied"));
    }
}
